/**
 * Chart colours, read from the CoOrdio design tokens at render time.
 *
 * WHY a hook rather than a `const PALETTE = ['#8B5CF6', …]` next to the chart: recharts needs a
 * concrete paint value for every series, and the obvious way to give it one is to type the hex in
 * TypeScript. Doing that pins the chart to a single theme — the token file redefines every colour
 * under `:root.dark`, so a hard-coded series colour keeps its light-mode value when the user flips
 * to dark and the chart stops matching the surface it sits on. The same is true of the Cortex
 * accent, which is a *product* override of `--color-accent-primary`.
 *
 * So the values are read from the cascade with `getComputedStyle(document.documentElement)`, and
 * re-read whenever the theme markers change. `ThemeProvider` publishes the active mode as both
 * `data-theme` on `<html>` and the `dark` class (see src/theme/ThemeProvider.tsx), so observing
 * those two attributes is enough to catch every theme switch, including the `system` mode following
 * the OS.
 *
 * The fallback when a token cannot be resolved — no DOM (SSR), or a test environment that does not
 * implement custom-property lookup — is the `var(--token)` reference itself rather than a literal.
 * SVG paint attributes accept `var()`, so a browser still paints the right colour, and no hex ever
 * enters this file.
 */

import { useEffect, useState } from 'react';

/**
 * Categorical series colours, in the order they should be assigned.
 * All eight are verified at >= 3:1 against `--color-bg-surface`, so any of them is legible as a
 * line, an area stroke or a pie slice without further checking.
 */
const CATEGORICAL_TOKENS = [
  '--color-data-1',
  '--color-data-2',
  '--color-data-3',
  '--color-data-4',
  '--color-data-5',
  '--color-data-6',
  '--color-data-7',
  '--color-data-8',
] as const;

/** Sequential (quantitative) scale, light to dark. For magnitude, not for identity. */
const SEQUENTIAL_TOKENS = [
  '--color-data-seq-1',
  '--color-data-seq-2',
  '--color-data-seq-3',
  '--color-data-seq-4',
  '--color-data-seq-5',
] as const;

/** The resolved token values a chart needs. */
export type ChartTokens = Readonly<{
  /** Categorical palette; index with `series[i % series.length]`. */
  series: readonly string[];
  /** Sequential palette for quantitative encodings. */
  sequential: readonly string[];
  /** Semantic states — use these instead of picking a green/amber/red out of `series`. */
  success: string;
  warning: string;
  error: string;
  /** Chart chrome. */
  axis: string;
  grid: string;
  /** Tooltip surface. */
  surface: string;
  border: string;
  text: string;
}>;

/**
 * Resolves one custom property against `<html>`, falling back to the `var()` reference so the
 * value stays theme-aware even when it cannot be read.
 */
function readToken(name: string): string {
  if (typeof document === 'undefined' || typeof window === 'undefined' || typeof window.getComputedStyle !== 'function') {
    return `var(${name})`;
  }

  const value = window.getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value || `var(${name})`;
}

/** Reads the whole set in one pass. */
function readChartTokens(): ChartTokens {
  return {
    series: CATEGORICAL_TOKENS.map(readToken),
    sequential: SEQUENTIAL_TOKENS.map(readToken),
    success: readToken('--color-success'),
    warning: readToken('--color-warning'),
    error: readToken('--color-error'),
    axis: readToken('--color-text-secondary'),
    grid: readToken('--color-border-default'),
    surface: readToken('--color-bg-elevated'),
    border: readToken('--color-border-default'),
    text: readToken('--color-text-primary'),
  };
}

/**
 * Design-token chart palette that follows the active theme.
 *
 * Re-reads on mount (stylesheets may not have applied when the first render happened) and on every
 * change to the `<html>` theme markers.
 */
export function useChartTokens(): ChartTokens {
  const [tokens, setTokens] = useState<ChartTokens>(readChartTokens);

  useEffect(() => {
    // A second read after mount: the first one can run before the token stylesheet is applied,
    // which would leave every colour on its `var()` fallback for the life of the component.
    setTokens(readChartTokens());

    if (typeof MutationObserver === 'undefined' || typeof document === 'undefined') return;

    const observer = new MutationObserver(() => setTokens(readChartTokens()));
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'data-theme'],
    });

    return () => observer.disconnect();
  }, []);

  return tokens;
}

/** Recharts `contentStyle` for a tooltip that sits on the product's elevated surface. */
export function chartTooltipStyle(tokens: ChartTokens) {
  return {
    background: tokens.surface,
    border: `1px solid ${tokens.border}`,
    borderRadius: 'var(--radius-lg)',
    color: tokens.text,
    fontSize: 12,
  } as const;
}

/** Recharts `tick` style for both axes. */
export function chartAxisTick(tokens: ChartTokens) {
  return { fontSize: 11, fill: tokens.axis } as const;
}
